import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';
import { Angulartics2GoogleTagManager } from 'angulartics2';

import { Resource } from '@shared/resource/models';
import {
  CreatePartyRequest,
  PublicCheckinRequest,
  PublicInfo,
  PublicResourcesResponse,
} from '@shared/public/models';
import { Notification, Party } from '@shared/party/models';
import { PartyTypes } from '@shared/party/enums';
import { ApiResponseModelWrapper } from '@tablesready/common-ui';
import { IdService } from './id.service';
import { ApiClientService } from '@tablesready/api-client';

export interface WaitlistPartyApiResponse {
  _id: string;
  size: number;
  type: PartyTypes;
  quoted_time: number;
  duration: number;
  services: string[];
  resources: string[];
  phone: string;
  name: string;
  checkin_time: number;
  waitlistId: string;
  restaurant: string;
  created_time: number;
  cargo: any;
  note: any;
  truck: string;
  carrier: string;
  deliveryId: string;
  resource: any;
  isHidden: boolean;
  notifications: Notification[];
  customFields: {[key: string]: string};
}

export interface GetPartyApiResponse {
  _id: string;
  waitlistId: string;
  size: number;
  type: PartyTypes.walkIn | PartyTypes.reservation;
  quoted_time: number;
  quoted_time_original?: number;
  canceled_time?: number;
  seated_time?: number;
  duration: number;
  loyalty_count: number;
  services: string[];
  resource?: string;
  resources?: string[];
  phone: string;
  name: string;
  restaurant: string;
  created_time: number;
  note?: string[] | string;
  customFields: {[key: string]: string};
  stripeData: {[key: string]: string};
}

@Injectable({ providedIn: 'root' })
export class PublicApiService {
  private readonly basePath = '/public';

  constructor(
    private http: ApiClientService,
    private idService: IdService,
    private angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
  ) {}

  getResources(username: string, workTime: Array<[number, number]>): Observable<Resource[]> {
    return this.http.post<PublicResourcesResponse>(`${this.basePath}/info/${username}/resources`, workTime)
      .pipe(
        map(({ success, data, error }) => {
          if (success) {
            return data;
          }

          throw { message: `${error}`, data };
        })
      );
  }

  getServiceProvider(username: string): Observable<PublicInfo | null> {
    return this.http
      .get<ApiResponseModelWrapper<PublicInfo>>(
        `/public/info/${username}`
      )
      .pipe(
        map(({ success, data, error }) => {
          if (success) {
            this.updateDataLayer(data);

            return data;
          }
          throw { message: `${error}`, data };
        })
      );
  }

  getWaitlistParties(username: string) {
    return this.http
      .get<ApiResponseModelWrapper<WaitlistPartyApiResponse[]>>(
        `/public/waitlist/${username}`
      )
      .pipe(
        map(({ success, data, error }) => {
          if (success) {
            return data;
          }
          throw new Error(`${error}`);
        })
      );
  }

  saveParty(model: CreatePartyRequest, username: string) {
    return this.http
      .post<ApiResponseModelWrapper<Party>>(`/public/waitlist/${username}`, model)
      .pipe(
        map(({ success, data, error, redirectUrl }) => {
          if (!success) {
            throw new Error(`${error}`);
          }
          return {data, redirectUrl};
        })
      );
  }

  getParty(username: string, id: string) {
    return this.http
      .get<ApiResponseModelWrapper<GetPartyApiResponse>>(
        `/public/party/${username}/${id}`
      )
      .pipe(
        map(({ success, data, error }) => {
          if (success) {
            return data;
          }
          throw new Error(`${error}`);
        })
      );
  }

  getParties(hours: Array<Array<number>>, username: string) {
    return this.http
      .post<ApiResponseModelWrapper<any>>('/public/parties', {
        hours,
        username,
      })
      .pipe(
        map(({ success, data, error }) => {
          if (success) {
            return data;
          }
          throw new Error(`${error}`);
        })
      );
  }

  deleteParty(partyId: string, performRefund = false) {
    return this.http.put<ApiResponseModelWrapper<any>>('/public/deleteParty', {
      _id: partyId,
      performRefund,
    })
      .pipe(
        map(({ success, data, error }) => {
          if (success) {
            return data;
          }
          throw new Error(`${error}`);
        })
      );
  }

  checkin(partyId: string, data: PublicCheckinRequest) {
    return this.http.post(`${this.basePath}/checkin/${partyId}`, data);
  }

  sendPartyVerificationCode(partyId: string, phone?: string) {
    return this.http.post(`${this.basePath}/verificationCode/${partyId}`, {phone});
  }

  completePayout(username: string, partyId: string, checkoutSessionId: string) {
    return this.http.post<any>(`${this.basePath}/waitlist/${username}/${partyId}/checkout`, {checkoutSessionId});
  }

  updateDataLayer(info: PublicInfo): void {
    if (info.id) {
      this.angulartics2GoogleTagManager.setUsername(info.id);

      const newData: any = {
        event: 'public-user',
        userId: info.mongoId || info.id,
      };

      this.angulartics2GoogleTagManager.pushLayer(newData);
    } else {
      this.angulartics2GoogleTagManager.setUsername(null);

      this.angulartics2GoogleTagManager.pushLayer({
        event: 'public-user',
        userId: null,
      });
    }
  }
}
